/* eslint-disable no-unused-vars */
import React, { ReactNode } from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import useWindowSize from '../../../utils/hook/useWindowSize';
import {
  darkBlue1, gray8, gray9, overlaySurface,
} from '../../colors/COLORS';
import ArrowRight from '../../icons/arrowRight';
import CloseSquareIcon from '../../icons/closeSquare';
import { header500 } from '../../typography';
import Scrollbar from '../scrollbar';
import ShortcutsModalHeader from '../shortcuts/components/ShortcutsModalHeader';

interface StyleProps {
  isTitleCenter?: boolean;
}

const Title = styled.span`
  ${header500};
  color: ${gray9};
  user-select: none;
`;

const TitleIconContainer = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ isTitleCenter }) => (isTitleCenter ? '-webkit-fill-available' : '')};

  svg {
    margin-left: 8px;
  }
`;

const IconWrapper = styled.button`
  display: flex;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;

  :hover {
    path {
      fill: ${gray8};
    }
  }
`;

const ModalHeader = styled.div<{ isHeadless: boolean }>`
  ${({ isHeadless }) => !isHeadless && `border-bottom: 2px solid ${darkBlue1};`};
  padding: 18px 24px;
`;

const ModalHeaderRowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

interface ModalProps {
  isOpen: boolean;
  setModalClosed: () => void
  title?: string;
  children: ReactNode;
  isShortcutsModal?: boolean;
  isScrollbarEnabled?: boolean;
  icon?: any,
  isHeadless?: boolean;
  isTitleCenter?: boolean;
  isOverflowHidden?: boolean;
  enableBackButton?: boolean,
  onBackButtonClick?: () => void,
  isExitButtonEnabled?: boolean,
  blurriness?: number,
  borderRadius?: string,
}

const Modal = ({
  isOpen, setModalClosed, title, isTitleCenter,
  children, isShortcutsModal, isScrollbarEnabled, icon, isHeadless = false,
  isOverflowHidden, enableBackButton, onBackButtonClick, isExitButtonEnabled = true,
  blurriness = 0, borderRadius = '10px',
}: ModalProps) => {
  const { isScreenSizeSM } = useWindowSize();
  return (
    <ReactModal
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={setModalClosed}
      style={{
        content: {
          // background: gray1,
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
          height: 'fit-content',
          inset: isScreenSizeSM ? '12vh calc((119vw - 457px)/2)' : '12vh calc((100vw - 457px)/2)',
          width: isScreenSizeSM ? '370px' : '457px',
          padding: 0,
          border: 'none',
          overflow: isOverflowHidden ? 'hidden' : '',
        },
        overlay: {
          zIndex: 20,
          background: overlaySurface,
          backdropFilter: `blur(${blurriness}px)`,
        },
      }}
      closeTimeoutMS={100}
    >
      {!isHeadless && (
        <ModalHeader isHeadless={isHeadless as boolean}>
          <ModalHeaderRowContainer>
            {enableBackButton
              && (
                <IconWrapper onClick={onBackButtonClick}>
                  <ArrowRight fill={gray9} rotateBy={180} />
                </IconWrapper>
              )}
            <TitleIconContainer isTitleCenter={isTitleCenter}>
              <Title>
                {!isHeadless && title}
              </Title>
              {icon}
            </TitleIconContainer>
            {isExitButtonEnabled && (
              <IconWrapper onClick={setModalClosed}>
                <CloseSquareIcon width="24" height="24" fill={gray9} />
              </IconWrapper>
            )}
          </ModalHeaderRowContainer>
          {isShortcutsModal && <ShortcutsModalHeader />}
        </ModalHeader>
      )}
      {isScrollbarEnabled
        ? (
          <Scrollbar maxHeight="77vh" borderRadius={borderRadius}>
            {children}
          </Scrollbar>
        ) : children}
    </ReactModal>
  );
};

Modal.defaultProps = {
  icon: null,
  title: '',
  isShortcutsModal: false,
  isScrollbarEnabled: true,
  isHeadless: false,
  isTitleCenter: false,
  isOverflowHidden: false,
  enableBackButton: false,
  onBackButtonClick: () => { },
  isExitButtonEnabled: true,
  blurriness: 0,
};

export default Modal;
